import React, { forwardRef, useImperativeHandle } from "react";
import { Autocomplete, TextField, Checkbox } from "@mui/material";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import PropTypes from "prop-types";

interface Option {
  label: string;
  value: any;
}

interface MultiSelectDropdownProps {
  label: string;
  fieldName: string;
  options: Option[];
  value: Option[]; // Selected values
  onChange: (field: string, value: any) => void;
  onInputChange?: (event: React.ChangeEvent<{}>, value: string) => void;
  onEnter?: (inputValue: string) => void; // New optional prop for handling Enter key.
  onBlur?: () => void;
  className?: string; // Optional className for styling
  placeholder?: string;
}

const MultiSelectDropdown = forwardRef(
  (
    { label, fieldName, options, value, onChange, onInputChange, onEnter, onBlur, className,placeholder}: MultiSelectDropdownProps,
    ref
  ) => {
    // Expose clear method to the parent component
    useImperativeHandle(ref, () => ({
      clear: () => {
        onChange(fieldName, []); // Clear all selected values
      },
    }));

    const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (event.key === "Enter") {
        event.preventDefault(); // Prevent default behavior
        const inputElement = event.target as HTMLInputElement;
        const inputValue = inputElement.value.trim();
        if (onEnter && inputValue) {
          onEnter(inputValue);
        }
      }

      if (event.key === " ") {
        event.preventDefault(); // Block space character
      }
    };

    return (
      <div>
        <Autocomplete
          className={className}
          multiple
          options={options}
          getOptionLabel={(option) => option.label}
          value={value}
          isOptionEqualToValue={(option, selectedValue) => option.value === selectedValue.value}
          disableCloseOnSelect
          onInputChange={onInputChange}
          onChange={(event, newValue) => {
            onChange(fieldName, newValue);
          }}
          renderOption={(props, option) => {
            const isSelected = value?.some(
              (selectedOption) => selectedOption.value === option.value
            );
            const { key, ...rest } = props;
            return (
              <li key={key} {...rest}>
                <Checkbox
                  icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
                  checkedIcon={<CheckBoxIcon fontSize="small" />}
                  checked={isSelected}
                />
                {option.label}
              </li>
            );
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              variant="outlined"
              label={label}
              placeholder={placeholder || `Select ${label}`}
              onKeyDown={handleKeyDown} // Block space and handle Enter
              onBlur={onBlur}
            />
          )}
          style={{ minWidth: 250 }}
        />
      </div>
    );
  }
);

MultiSelectDropdown.propTypes = {
  label: PropTypes.string.isRequired,
  fieldName: PropTypes.string.isRequired,
  options: PropTypes.array.isRequired,
  value: PropTypes.array.isRequired,
  onChange: PropTypes.func.isRequired,
  onInputChange: PropTypes.func,
  onEnter: PropTypes.func,
  onBlur: PropTypes.func,
  placeholder: PropTypes.string,
};

export default MultiSelectDropdown;